import React, { useState, useEffect, useRef } from 'react';
import '../Dashboard.css';
import { API_ENDPOINTS, apiRequest } from '../../../api/config';
import { getTranslation } from '../translations';
import { useTheme } from '../ThemeContext';

// 编辑斗篷弹窗组件
const EditCloakModal = ({ isOpen, onClose, onSubmit, initialData }) => {
  const { language } = useTheme();
  const t = getTranslation(language);

  const [formData, setFormData] = useState({
    name: '',
    realUrl: '',
    auditUrl: '',
    mode: 'cloak',
    selectedRegions: [],
    platforms: []
  });

  const [continentData, setContinentData] = useState([]);
  const [selectedContinent, setSelectedContinent] = useState('');
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const regionSelectorRef = useRef(null);

  // 点击外部关闭地区下拉框
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (regionSelectorRef.current && !regionSelectorRef.current.contains(e.target)) {
        setShowRegionDropdown(false);
      }
    };
    if (showRegionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRegionDropdown]);

  // 如果是编辑模式，初始化表单数据
  useEffect(() => {
    if (initialData) {
      // 确保selectedRegions中的每个region都有完整的code和name
      const processedRegions = (initialData.selectedRegions || []).map(region => {
        if (!region) return null;
        
        // 如果region已经有name，直接使用
        if (region.name && region.name !== region.code) {
          return {
            code: region.code || '',
            name: region.name
          };
        }
        
        // 如果region只有code（代码），需要从continentData中查找名称
        // 注意：这可能在continentData加载之前执行，所以需要后续处理
        return {
          code: region.code || '',
          name: region.name || region.code || ''
        };
      }).filter(Boolean);
      
      setFormData({
        name: initialData.name || '',
        realUrl: initialData.realUrl || '',
        auditUrl: initialData.auditUrl || '',
        mode: initialData.mode || 'cloak',
        selectedRegions: processedRegions,
        platforms: initialData.platforms || []
      });
    }
  }, [initialData]);

  // 使用 ref 跟踪是否已加载数据，防止 StrictMode 重复调用
  const hasLoadedRef = useRef(false);

  // 从后端加载按洲分组的国家列表
  useEffect(() => {
    if (isOpen && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchRegions();
    }
    // 弹窗关闭时重置标记
    if (!isOpen) {
      hasLoadedRef.current = false;
    }
  }, [isOpen]);

  // 当continentData加载完成后，补充selectedRegions中的地区名称
  useEffect(() => {
    if (continentData.length > 0 && formData.selectedRegions.length > 0) {
      // 检查是否有地区缺少名称
      const needsUpdate = formData.selectedRegions.some(region => 
        !region.name || region.name === region.code
      );
            
      if (needsUpdate) {
        const updatedRegions = formData.selectedRegions.map(region => {
          // 如果已经有完整的名称，保持不变
          if (region.name && region.name !== region.code) {
            return region;
          }
                
          // 从 continentData 中查找完整的国家信息
          for (const continent of continentData) {
            const country = continent.countries.find(c => c.code === region.code);
            if (country) {
              return {
                code: country.code,
                name: country.name
              };
            }
          }
                
          // 如果找不到，保持原样
          return region;
        });
              
        // 只有当确实有更新时才设置
        const hasChanges = updatedRegions.some((region, index) => 
          region.name !== formData.selectedRegions[index]?.name
        );
              
        if (hasChanges) {
          setFormData(prev => ({
            ...prev,
            selectedRegions: updatedRegions
          }));
        }
      }
    }
  }, [continentData]);

  const fetchRegions = async () => {
    setLoadingRegions(true);
    try {
      const response = await apiRequest(`/api/cloak/countries/by-continent?lang=${language}`);
      const data = response?.data || response;

      if (Array.isArray(data)) {
        setContinentData(data);
        if (data.length > 0) {
          setSelectedContinent(data[0].continentCode);
        }
      } else {
        setContinentData([]);
      }
    } catch (error) {
      console.error('Error getting country list:', error);
      setErrors(prev => ({ ...prev, regions: t('loadCountryFailed') }));
    } finally {
      setLoadingRegions(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (errors[name]) {
      const updatedErrors = {...errors};
      delete updatedErrors[name];
      setErrors(updatedErrors);
    }

    if (name === 'mode' && value !== 'cloak' && formData.mode === 'cloak') {
      setFormData({
        ...formData,
        [name]: value,
        selectedRegions: []
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // 处理地区选择
  const handleRegionChange = (countryCode) => {
    let countryObject = null;
    for (const continent of continentData) {
      const country = continent.countries.find(c => c.code === countryCode);
      if (country) {
        countryObject = {
          code: country.code,
          name: country.name
        };
        break;
      }
    }

    if (!countryObject) {
      // 如果在continentData中找不到，尝试使用已有的region数据
      const existingRegion = formData.selectedRegions.find(r => r.code === countryCode);
      if (existingRegion) {
        countryObject = existingRegion;
      } else {
        return;
      }
    }

    const isSelected = formData.selectedRegions.some(region => region.code === countryCode);

    let newSelectedRegions;
    if (isSelected) {
      newSelectedRegions = formData.selectedRegions.filter(region => region.code !== countryCode);
    } else {
      newSelectedRegions = [...formData.selectedRegions, countryObject];
    }

    setFormData({
      ...formData,
      selectedRegions: newSelectedRegions
    });

    if (errors.selectedRegions) {
      const updatedErrors = {...errors};
      delete updatedErrors.selectedRegions;
      setErrors(updatedErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('requiredField');
    }

    if (!formData.realUrl.trim()) {
      newErrors.realUrl = t('requiredField');
    } else if (!isValidUrl(formData.realUrl)) {
      newErrors.realUrl = t('enterValidUrl');
    }

    if (formData.mode !== 'fullOpen' && !formData.auditUrl.trim()) {
      newErrors.auditUrl = t('requiredField');
    } else if (formData.auditUrl.trim() && !isValidUrl(formData.auditUrl)) {
      newErrors.auditUrl = t('enterValidUrl');
    }

    if (formData.mode === 'cloak' && formData.selectedRegions.length === 0) {
      newErrors.selectedRegions = t('selectAtLeastOneRegion');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        selectedRegions: formData.selectedRegions.map(region => region.code)
      };

      console.log('Edit submit data:', submitData);
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Failed to process form data:', error);
      setErrors({ submit: t('processDataFailed') });
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay open" onClick={(e) => e.target === e.currentTarget && onClose()} style={{opacity: 1, pointerEvents: 'auto'}}>
      <div className="modal" style={{width: '744px', maxHeight: '92vh'}}>
        <div className="modal-hd">
          <span className="modal-title">{t('editCloak')}</span>
          <span className="modal-close" onClick={onClose}>×</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{padding: '20px', overflowY: 'auto', maxHeight: 'calc(92vh - 160px)'}}>
            {errors.submit && (
              <div className="form-error">{errors.submit}</div>
            )}

            {/* 斗篷名称 */}
            <div className="form-group">
              <label className="form-label required">{t('cloakName')}</label>
              <input
                type="text"
                id="name"
                name="name"
                className={`fi ${errors.name ? 'fi-error' : ''}`}
                placeholder="输入斗篷名称"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <p className="form-error">{errors.name}</p>}
            </div>

            {/* 真实链接 */}
            <div className="form-group">
              <label className="form-label required">{t('realLink')}</label>
              <input
                type="text"
                id="realUrl"
                name="realUrl"
                className={`fi ${errors.realUrl ? 'fi-error' : ''}`}
                placeholder="https://example.com"
                value={formData.realUrl}
                onChange={handleChange}
              />
              {errors.realUrl && <p className="form-error">{errors.realUrl}</p>}
            </div>

            {/* 审核链接 */}
            <div className="form-group">
              <label className="form-label">{t('auditLink')} {formData.mode !== 'fullOpen' && <span style={{color: 'var(--red)'}}>*</span>}</label>
              <div style={{display: 'flex', gap: '8px'}}>
                <select
                  className="fi"
                  style={{width: 'auto', minWidth: '140px'}}
                  onChange={(e) => {
                    setFormData({...formData, auditUrl: e.target.value});
                    if (errors.auditUrl) {
                      const updatedErrors = {...errors};
                      delete updatedErrors.auditUrl;
                      setErrors(updatedErrors);
                    }
                  }}
                  value={(() => {
                    const url = formData.auditUrl || '';
                    if (url.includes('amazon.com')) return 'https://www.amazon.com/';
                    if (url.includes('gog.com')) return 'https://www.gog.com/en/';
                    if (url.includes('ottogroup.com')) return 'https://www.ottogroup.com/';
                    if (url.includes('shopify.com')) return 'https://www.shopify.com/';
                    if (url.includes('linkedin.com')) return 'https://www.linkedin.com/';
                    if (url.includes('kyotographie.jp')) return 'https://www.kyotographie.jp/';
                    if (url.includes('crazygames.com')) return 'https://www.crazygames.com/';
                    if (url.includes('paypal.com')) return 'https://www.paypal.com/';
                    if (url.includes('discord.com')) return 'https://discord.com/';
                    return '';
                  })()}
                >
                  <option value="">{t('customOption')}</option>
                  <option value="https://www.amazon.com/">{t('defaultOption')}</option>
                  <option value="https://www.gog.com/en/">{t('gamingCategory')}</option>
                  <option value="https://www.ottogroup.com/">{t('marketingCategory')}</option>
                  <option value="https://www.shopify.com/">{t('shopCategory')}</option>
                  <option value="https://www.linkedin.com/">{t('corporateCategory')}</option>
                  <option value="https://www.kyotographie.jp/">{t('photographyCategory')}</option>
                  <option value="https://www.crazygames.com/">{t('miniGamesCategory')}</option>
                  <option value="https://www.paypal.com/">{t('financeCategory')}</option>
                  <option value="https://discord.com/">{t('toolCategory')}</option>
                </select>

                <input
                  type="text"
                  id="auditUrl"
                  name="auditUrl"
                  className={`fi ${errors.auditUrl ? 'fi-error' : ''}`}
                  style={{flex: 1, minWidth: 0, width: 'auto'}}
                  placeholder="https://example.com/safe"
                  value={formData.auditUrl}
                  onChange={handleChange}
                />
              </div>
              {errors.auditUrl && (
                <p className="form-error">{errors.auditUrl}</p>
              )}
            </div>

            {/* 模式选择 */}
            <div className="form-group">
              <label className="form-label required">{t('mode')}</label>
              <div className="mode-grid">
                <div
                  className={`mode-card ${formData.mode === 'cloak' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, mode: 'cloak'})}
                >
                  <div className="mode-card-title">
                    <input
                      type="radio"
                      name="mode"
                      id="mode-cloak"
                      value="cloak"
                      checked={formData.mode === 'cloak'}
                      onChange={handleChange}
                    />
                    <label htmlFor="mode-cloak">{t('cloakMode')}</label>
                  </div>
                  <p className="mode-card-desc">{t('cloakModeDesc')}</p>
                </div>

                <div
                  className={`mode-card ${formData.mode === 'fullOpen' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, mode: 'fullOpen'})}
                >
                  <div className="mode-card-title">
                    <input
                      type="radio"
                      name="mode"
                      id="mode-fullOpen"
                      value="fullOpen"
                      checked={formData.mode === 'fullOpen'}
                      onChange={handleChange}
                    />
                    <label htmlFor="mode-fullOpen">{t('fullOpenMode')}</label>
                  </div>
                  <p className="mode-card-desc">{t('fullOpenModeDesc')}</p>
                </div>

                <div
                  className={`mode-card ${formData.mode === 'audit' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, mode: 'audit'})}
                >
                  <div className="mode-card-title">
                    <input
                      type="radio"
                      name="mode"
                      id="mode-audit"
                      value="audit"
                      checked={formData.mode === 'audit'}
                      onChange={handleChange}
                    />
                    <label htmlFor="mode-audit">{t('auditMode')}</label>
                  </div>
                  <p className="mode-card-desc">{t('auditModeDesc')}</p>
                </div>
              </div>
            </div>

            {/* 投放平台选择 */}
            <div className="form-group">
              <label className="form-label">{t('adPlatform')}</label>
              <div className="platform-grid">
                {[
                  { id: 'facebook', label: 'Facebook', icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  )},
                  { id: 'tiktok', label: 'TikTok', icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.75a8.16 8.16 0 0 0 4.77 1.52V6.82a4.85 4.85 0 0 1-1-.13z"/>
                    </svg>
                  )},
                  { id: 'google', label: 'Google', icon: (
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  )}
                ].map(platform => {
                  const isSelected = formData.platforms.includes(platform.id);
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => {
                        const newPlatforms = isSelected
                          ? formData.platforms.filter(p => p !== platform.id)
                          : [...formData.platforms, platform.id];
                        setFormData({ ...formData, platforms: newPlatforms });
                      }}
                      className={`platform-btn ${isSelected ? 'selected' : ''}`}
                    >
                      {platform.icon}
                      {platform.label}
                    </button>
                  );
                })}
              </div>
              <p className="form-hint">{t('adPlatformHint')}</p>
            </div>

            {/* 地区选择 - 仅在斗篷模式下显示 */}
            {formData.mode === 'cloak' && (
              <div className="form-group">
                <label className="form-label required">{t('targetRegions')}</label>
                <div className="region-selector" ref={regionSelectorRef}>
                  {/* 洲选择器 */}
                  <div style={{marginBottom: '12px'}}>
                    <label className="form-label">{t('selectContinent')}</label>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                      {continentData.map((continent) => (
                        <button
                          key={continent.continentCode}
                          type="button"
                          onClick={() => setSelectedContinent(continent.continentCode)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            background: selectedContinent === continent.continentCode ? 'var(--accent)' : 'var(--bg-input)',
                            color: selectedContinent === continent.continentCode ? '#fff' : 'var(--t1)',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {continent.continentName} ({continent.countries.length})
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 已选择的国家显示 */}
                  <div
                    className={`fi ${errors.selectedRegions ? 'fi-error' : ''}`}
                    style={{minHeight: '42px', display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px 10px'}}
                    onClick={() => setShowRegionDropdown(!showRegionDropdown)}
                  >
                    {loadingRegions ? (
                      <span style={{fontSize: '12px', color: 'var(--t3)'}}>{t('loadingCountries')}</span>
                    ) : formData.selectedRegions.length > 0 ? (
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                        {formData.selectedRegions.map(region => {
                          // Ensure we're rendering a valid string
                          const displayName = region.name || region.code || (typeof region === 'string' ? region : '');
                          return (
                            <span
                              key={region.code}
                              className="region-tag"
                            >
                              {displayName}
                              <span
                                className="region-tag-remove"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRegionChange(region.code);
                                }}
                              >×</span>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{color: 'var(--t3)'}}>{t('selectRegion')}</span>
                    )}
                    <span style={{marginLeft: 'auto', color: 'var(--t2)'}}>
                      {showRegionDropdown ? '▲' : '▼'}
                    </span>
                  </div>

                  {showRegionDropdown && (
                    <div className="region-dropdown">
                      {loadingRegions ? (
                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'}}>
                          <span className="spinner"></span>
                          <span style={{marginLeft: '8px', fontSize: '12px'}}>{t('loadingCountries')}</span>
                        </div>
                      ) : (() => {
                        const currentContinent = continentData.find(c => c.continentCode === selectedContinent);
                        const countries = currentContinent ? currentContinent.countries : [];

                        return countries.length > 0 ? (
                          <div style={{padding: '8px'}}>
                            <div style={{fontSize: '11px', color: 'var(--t3)', marginBottom: '8px', padding: '0 4px'}}>
                              {currentContinent.continentName} - {t('selectRegion')}
                            </div>
                            {countries.map(country => (
                              <div
                                key={country.code}
                                className="region-item"
                                onClick={() => {
                                  handleRegionChange(country.code);
                                }}
                              >
                                <input
                                  type="checkbox"
                                  id={`region-${country.code}`}
                                  checked={formData.selectedRegions.some(r => r.code === country.code)}
                                  onChange={() => {}}
                                  style={{marginRight: '8px'}}
                                />
                                <label htmlFor={`region-${country.code}`} style={{cursor: 'pointer', flex: 1}}>
                                  {country.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{padding: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--t3)'}}>
                            {t('noCountries')}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {errors.selectedRegions && <p className="form-error">{errors.selectedRegions}</p>}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-default"
              onClick={onClose}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  保存中...
                </>
              ) : t('saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return modalContent;
};

export default EditCloakModal;
